import React from "react";

/* Dashboard — live scanning state.

   Mission-control view shown while a scan is in progress for a first-time
   user (or any time the inbox is empty). All contextual labels — profile
   name, subreddit list, themes, avoid count — come from the user's real
   business profile. The per-subreddit progress bars, candidate counters,
   and activity stream are driven by a client-side clock because the
   backend doesn't currently stream per-subreddit events; the clock is
   sized to a realistic duration and held at ~95% if the real scan is
   still running, so the view never reads as "done" before the scan
   actually completes. */

const THREADS_PER_SUB = 92;

function DashboardScanning({ profile, isFirstScan = false, scanSubreddits = null }) {
  // Latch the first-scan flag and profile name on mount. Both can change
  // mid-scan as opportunities start arriving (firstScan flips false) or the
  // profile briefly reloads — which made the title flicker between its two
  // forms. Holding the initial values keeps the heading constant for the
  // life of the view.
  const isFirstScanRef = React.useRef(isFirstScan);
  const scanTargetName = React.useRef(profile?.name || "your profile");
  const firstScan = isFirstScanRef.current;
  const targetName = scanTargetName.current;

  // List only the subreddits this scan actually targeted. `scanSubreddits` is
  // the subset the user selected in the scan dialog; fall back to the full
  // profile radar when it's unavailable (e.g. after a reload mid-scan).
  const subs = React.useMemo(() => {
    const norm = (s) => (String(s).startsWith("r/") ? String(s) : "r/" + String(s).replace(/^\/?r\//i, ""));
    if (Array.isArray(scanSubreddits) && scanSubreddits.length) {
      return scanSubreddits.map(norm);
    }
    return (profile?.subreddits || []).map(norm);
  }, [profile, scanSubreddits]);

  // Estimated duration scales with subreddit count. Real scans usually
  // finish inside this window; if they don't, the timer caps at 95% and
  // waits for the parent to flip isScanning off.
  const estimatedDurationMs = React.useMemo(
    () => Math.max(45_000, 30_000 + subs.length * 8_000),
    [subs.length]
  );

  // Live clock — ticks while the parent keeps this view mounted.
  const startedAtRef = React.useRef(Date.now());
  const [now, setNow] = React.useState(Date.now());
  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 80);
    return () => clearInterval(id);
  }, []);

  const elapsed = now - startedAtRef.current;
  const rawPct = Math.min(elapsed / estimatedDurationMs, 1);
  // Hold at 95% so the bar doesn't claim completion before the real scan
  // is done — the parent unmounts this view when isScanning flips to false.
  const pct = Math.min(rawPct, 0.95);
  const elapsedLabel = formatScanElapsed(elapsed);
  const remainingSec = Math.max(0, (estimatedDurationMs - elapsed) / 1000);

  // Per-subreddit progress. Each sub starts after a stagger and runs for
  // ~55% of the scan duration. Targets are deterministic per index so the
  // view doesn't flicker between renders.
  const subData = React.useMemo(() => {
    return subs.map((s, i) => {
      const start = (i / Math.max(1, subs.length)) * 0.45;
      const end = Math.min(1, start + 0.55);
      const target = 88 + ((i * 31) % 52);
      return { sub: s, start, end, target, threadsPerSub: THREADS_PER_SUB };
    });
  }, [subs]);

  const subProgress = subData.map((d) => {
    if (pct < d.start) return { ...d, status: "queued", localPct: 0, candidates: 0 };
    if (pct >= d.end) return { ...d, status: "done", localPct: 1, candidates: d.target };
    const local = (pct - d.start) / (d.end - d.start);
    const eased = 1 - Math.pow(1 - local, 1.6);
    const candidates = Math.floor(eased * d.target);
    return { ...d, status: "scanning", localPct: eased, candidates };
  });

  const totalCandidates = subProgress.reduce((a, s) => a + s.candidates, 0);
  const completedSubs = subProgress.filter((s) => s.status === "done").length;
  const threadsScanned = Math.floor(pct * Math.max(1, subs.length) * THREADS_PER_SUB);

  const events = React.useMemo(
    () => buildEventTimeline(subData, profile),
    [subData, profile]
  );
  const visibleEvents = events.filter((e) => pct >= e.at).slice(-7).reverse();

  const phase =
    pct < 0.08 ? "Initializing"
    : pct < 0.55 ? "Crawling threads"
    : pct < 0.85 ? "Scoring matches"
    : "Finalizing";

  const useCaseBlurb = (profile?.useCases || [])
    .slice(0, 2)
    .join(", ")
    .toLowerCase();

  return (
    <div className="page page-v2 sn-page screen-enter">
      <section className="sn-hero">
        <div className="sn-hero-copy">

          <h1 className="sn-title">
            {firstScan
              ? <>Running first scan for <em style={{letterSpacing: "0.08rem"}}>{targetName}</em></>
              : <>Scanning <em style={{letterSpacing: "0.08rem"}}>{targetName}</em> for new opportunities</>}
          </h1>
          <p className="sn-subtitle">
            Sweeping <strong>{subs.length} subreddit{subs.length === 1 ? "" : "s"}</strong>{" "}
            for conversations. Scoring candidates against your keywords and avoid list.
            
          </p>

          <div className="sn-progress">
            <div className="sn-progress-track">
              <div className="sn-progress-fill" style={{ width: `${pct * 100}%` }} />
              <div
                className="sn-progress-shimmer"
                style={{ left: `${Math.min(pct * 100, 98)}%` }}
              />
            </div>
            <div className="sn-progress-meta">
              <span className="sn-pm-item">
                <window.I.Clock size={12} />
                <span className="mono">{elapsedLabel}</span> elapsed
              </span>
              <span className="sn-pm-sep" />
              <span className="sn-pm-item">
                <span className="mono">{Math.round(pct * 100)}%</span> complete
              </span>
            </div>
          </div>
        </div>

        <div className="sn-hero-viz">
          <ScanRadar subs={subs} subProgress={subProgress} profile={profile} />
        </div>
      </section>

      <section className="sn-stats">
        <LiveStat
          label="Subreddits"
          value={`${completedSubs}/${subs.length}`}
          meta={completedSubs === subs.length ? "all swept" : "in progress"}
          icon="Layers"
        />
        <LiveStat
          label="Threads scanned"
          value={threadsScanned}
          meta="across all sources"
          icon="Search"
        />
        <LiveStat
          label="Candidates"
          value={totalCandidates}
          meta="passed first filter"
          icon="Sparkles"
        />
      </section>

      <div className="sn-grid">
        <div className="card sn-card">
          <div className="card-header card-header-v2">
            <window.I.ChartBar size={14} style={{ color: "var(--text-tertiary)" }} />
            <span className="card-title">Coverage</span>
            <div className="spacer" />
            <span className="sn-card-meta mono">
              {completedSubs}/{subs.length} subs · {totalCandidates} candidates
            </span>
          </div>
          <div className="sn-coverage">
            {subProgress.map((d) => (
              <SubScanRow key={d.sub} d={d} />
            ))}
          </div>
        </div>

        <div className="card sn-card">
          <div className="card-header card-header-v2">
            <span className="sn-live-pulse" />
            <span className="card-title">Live activity</span>
            <div className="spacer" />
            <span className="sn-card-meta mono">
              {events.filter((e) => pct >= e.at).length} events
            </span>
          </div>
          <div className="sn-activity">
            {visibleEvents.length === 0 && (
              <div className="sn-activity-empty">Waiting for the first event…</div>
            )}
            {visibleEvents.map((e) => (
              <ActivityLine key={e.key} ev={e} />
            ))}
          </div>
        </div>
      </div>

      <div className="sn-foot">
        <window.I.ShieldCheck size={13} />
        Read-only sweep.
        <span className="sn-foot-sep" />
        <span className="sn-foot-meta">Page will automatically refresh when scan is complete.</span>
      </div>
    </div>
  );
}

function LiveStat({ label, value, meta, icon, accent, tick }) {
  const IconCmp = icon ? window.I[icon] : null;
  return (
    <div className={"sn-stat " + (accent ? "sn-stat-accent" : "")}>
      <div className="sn-stat-head">
        {IconCmp && <IconCmp size={13} />}
        <span className="sn-stat-label">{label}</span>
        {tick && <span className="sn-stat-tick" aria-hidden="true" />}
      </div>
      <div className="sn-stat-value mono">{value}</div>
      <div className="sn-stat-meta">{meta}</div>
    </div>
  );
}

function SubScanRow({ d }) {
  const name = d.sub.replace(/^r\//, "");
  const initial = name.charAt(0).toUpperCase();
  const color = snSubColor(d.sub);

  return (
    <div className={"sn-sub-row sn-sub-row-" + d.status}>
      <div className="sn-sub-head">
        <span className="sn-sub-avatar" style={{ background: color }}>
          {initial}
        </span>
        <span className="sn-sub-name mono">{d.sub}</span>
        <span className="sn-sub-status">
          {d.status === "queued" && <><span className="sn-sub-status-dot is-queued" /> queued</>}
          {d.status === "scanning" && <><span className="sn-sub-status-dot is-scanning" /> scanning…</>}
          {d.status === "done" && <><window.I.Check size={11} style={{ color: "var(--success)" }} /> swept</>}
        </span>
        <div className="spacer" />
        {d.candidates > 0 && (
          <span className="sn-sub-nums mono">
            <span className="sn-sub-candidates">{d.candidates}</span>
            <span className="sn-sub-num-label">candidates</span>
          </span>
        )}
      </div>
      <div className="sn-sub-track">
        <div className="sn-sub-fill" style={{ width: `${d.localPct * 100}%` }} />
        {d.status === "scanning" && (
          <div className="sn-sub-head-pulse" style={{ left: `${d.localPct * 100}%` }} />
        )}
      </div>
    </div>
  );
}

function ActivityLine({ ev }) {
  const IconCmp = window.I[ev.icon] || window.I.Info;
  return (
    <div className={"sn-activity-row sn-activity-" + ev.kind}>
      <div className="sn-activity-icon">
        <IconCmp size={11} />
      </div>
      <div className="sn-activity-body">
        <span className="sn-activity-text">{ev.text}</span>
        {ev.detail && <span className="sn-activity-detail mono">{ev.detail}</span>}
      </div>
    </div>
  );
}

function ScanRadar({ subs, subProgress, profile }) {
  if (!subs.length) return null;
  const subredditMeta = React.useMemo(() => {
    const byName = new Map();
    for (const item of profile?._server?.search_plan?.suggested_subreddits || []) {
      const clean = String(item?.name || "").trim().replace(/^r\//i, "").toLowerCase();
      if (!clean) continue;
      byName.set(clean, {
        communityIcon: item?.community_icon || "",
      });
    }
    for (const item of profile?.subreddits || []) {
      if (!item || typeof item === "string") continue;
      const clean = String(item?.name || item?.subreddit || "").trim().replace(/^r\//i, "").toLowerCase();
      if (!clean) continue;
      byName.set(clean, {
        ...byName.get(clean),
        communityIcon: item?.community_icon || item?.communityIcon || byName.get(clean)?.communityIcon || "",
      });
    }
    return byName;
  }, [profile]);
  const positions = subs.map((s, i) => {
    const angle = (-90 + (360 / subs.length) * i) * (Math.PI / 180);
    return {
      sub: s,
      x: 50 + Math.cos(angle) * 36,
      y: 50 + Math.sin(angle) * 36,
    };
  });

  return (
    <div className="sn-radar" aria-hidden="true">
      <div className="sn-radar-rings">
        <span className="sn-radar-ring sn-radar-ring-1" />
        <span className="sn-radar-ring sn-radar-ring-2" />
        <span className="sn-radar-ring sn-radar-ring-3" />
        <span className="sn-radar-ring sn-radar-ring-4" />
      </div>

      <div className="sn-radar-sweep" />
      <div className="sn-radar-axis sn-radar-axis-h" />
      <div className="sn-radar-axis sn-radar-axis-v" />

      <div className="sn-radar-core">
        <div
          className="sn-radar-core-mark"
          style={!profile?.faviconUrl ? { background: profile?.color || "var(--accent)" } : undefined}
        >
          {profile?.faviconUrl ? (
            <img className="profile-mark-img" src={profile.faviconUrl} alt="" />
          ) : (
            profile?.initials || "•"
          )}
        </div>
      </div>

      {positions.map((p, i) => {
        const status = subProgress[i]?.status || "queued";
        const icon = subredditMeta.get(p.sub.replace(/^r\//, "").toLowerCase())?.communityIcon || "";
        return (
          <div
            key={p.sub}
            className={"sn-radar-sat sn-radar-sat-" + status}
            style={{ left: `${p.x}%`, top: `${p.y}%` }}
          >
            <span className="sn-radar-sat-dot" style={icon ? undefined : { background: snSubColor(p.sub) }}>
              {icon ? (
                <img className="radar-sat-icon" src={icon} alt="" loading="lazy" />
              ) : (
                p.sub.replace(/^r\//, "").charAt(0).toUpperCase()
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}

const SN_SUB_PALETTE = [
  "oklch(0.62 0.13 268)",
  "oklch(0.6 0.13 200)",
  "oklch(0.62 0.12 160)",
  "oklch(0.65 0.13 50)",
  "oklch(0.6 0.14 340)",
  "oklch(0.58 0.13 30)",
];
function snSubColor(name) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return SN_SUB_PALETTE[Math.abs(h) % SN_SUB_PALETTE.length];
}

function formatScanElapsed(elapsedMs) {
  const totalSec = elapsedMs / 1000;
  if (totalSec < 100) {
    return `${totalSec.toFixed(1)}s`;
  }
  const min = Math.floor(totalSec / 60);
  const sec = (totalSec % 60).toFixed(1);
  const [secInt, secDec = "0"] = sec.split(".");
  return `${min}.${secInt.padStart(2, "0")}.${secDec}`;
}

function buildEventTimeline(subData, profile) {
  const subs = subData.map((d) => d.sub);
  const events = [];
  let key = 0;

  events.push({
    at: 0.01,
    kind: "system",
    icon: "Sparkles",
    text: "Loading profile",
    detail: profile?.name,
  });
  events.push({
    at: 0.04,
    kind: "system",
    icon: "Layers",
    text: `Targeting ${subs.length} subreddit${subs.length === 1 ? "" : "s"}`,
    detail: subs.join(" · "),
  });

  subData.forEach((d) => {
    events.push({
      at: d.start + 0.005,
      kind: "fetch",
      icon: "Search",
      text: `${d.sub} → crawling newest threads`,
    });
    events.push({
      at: d.start + (d.end - d.start) * 0.35,
      kind: "score",
      icon: "ChartBar",
      text: `${d.sub} → scoring candidates`,
      detail: `${Math.floor(d.threadsPerSub * 0.65)}+ threads`,
    });
    events.push({
      at: d.end - 0.005,
      kind: "done",
      icon: "Check",
      text: `${d.sub} → swept (${d.target} candidates)`,
    });
  });

  const avoidCount = (profile?.avoid || []).length;
  if (avoidCount > 0) {
    events.push({
      at: 0.92,
      kind: "system",
      icon: "Filter",
      text: `Applying avoid list (${avoidCount} term${avoidCount === 1 ? "" : "s"})`,
    });
  }
  events.push({
    at: 0.97,
    kind: "system",
    icon: "Sparkles",
    text: "Ranking final results",
  });

  return events
    .sort((a, b) => a.at - b.at)
    .map((e) => ({ ...e, key: key++ }));
}

window.DashboardScanning = DashboardScanning;
